import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./SACompanyForm.scss";
import { getAllExistPlans, addCompanyBySuperAdmin, updateCompanyBySuperAdmin, getAllRightsEdit, getCustomRights, createOrUpdateCustomRights } from "../../services/api/SuperAdminApi";
import { getCompanyDataByID } from "../../services/api/CompanyApi";
import { useLoading } from "../../context/LoadingContext";
import { useToast } from "../../context/ToastContext";

export const SACompanyForm = () => {
  const navigate = useNavigate();
  const { id: companyId } = useParams();
  const { showToast } = useToast();
  const { showLoading, hideLoading } = useLoading();

  // Static default rights structure
  const getDefaultCustomRights = useCallback(() => ({
    cases: false,
    clients: false,
    expenses: false,
    invoices: false,
    labels: false,
    mobileAppAccess: false,
    notes: false,
    tasks: false,
    teams: false,
    branch: false
  }), []);

  const [formData, setFormData] = useState({
    companyName: "",
    address: "",
    state: "",
    zip: "",
    country: "",
    email: "",
    phno: "",
    website: "",
    planId: "",
    gstNo: "",
    // vatNo: "",
    isActive: true,
    subscription: {
      currentPeriodStart: "",
      currentPeriodEnd: "",
      paymentMethod: "",
      paymentId: "",
      paymentStatus: "",
      amountPaid: ""
    }
  });

  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [plans, setPlans] = useState([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [dynamicRights, setDynamicRights] = useState(null);
  const [isLoadingRights, setIsLoadingRights] = useState(false);
  const [selectedPlanType, setSelectedPlanType] = useState("");
  
  // Override Plan Rights state
  const [overridePlanRights, setOverridePlanRights] = useState(false);
  const [customRights, setCustomRights] = useState(null);
  const [isLoadingCustomRights, setIsLoadingCustomRights] = useState(false);
  const [isSavingCustomRights, setIsSavingCustomRights] = useState(false);

  // Helper function to format datetime for input field (IST)
  const formatDateTimeForInput = (dateString) => {
    if (!dateString) return '';
    // Create date from UTC string
    const utcDate = new Date(dateString);
    // Convert to IST by adding 5.5 hours
    const istDate = new Date(utcDate.getTime() + (5.5 * 60 * 60 * 1000));
    // Format for datetime-local input (YYYY-MM-DDTHH:MM)
    const year = istDate.getUTCFullYear();
    const month = String(istDate.getUTCMonth() + 1).padStart(2, '0');
    const day = String(istDate.getUTCDate()).padStart(2, '0');
    const hours = String(istDate.getUTCHours()).padStart(2, '0');
    const minutes = String(istDate.getUTCMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Fetch plans on component mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setIsLoadingPlans(true);
        const response = await getAllExistPlans();
        setPlans(response.data.result || []);
      } catch (error) {
        console.error('Error fetching plans:', error);
        showToast("Failed to load plans. Please refresh the page.", "error");
      } finally {
        setIsLoadingPlans(false);
      }
    };
    fetchPlans();
  }, [showToast]);

  // Fetch company details if editing
  useEffect(() => {
    if (companyId) {
      setIsEditMode(true);
      showLoading('Loading company details...');
      getCompanyDataByID(companyId)
        .then(res => {
          const company = res.data?.company || {};
          console.log('Company data received:', company);
          console.log('Subscription data:', company.subscription);
          setFormData(prev => ({
            companyName: company.companyName || "",
            address: company.address || "",
            state: company.state || "",
            zip: company.zip || "",
            country: company.country || "",
            email: company.email || "",
            phno: company.phno || "",
            website: company.website || "",
            planId: company.planId || company.razorpayPlanId || "",
            gstNo: company.gstNo || "",
            // vatNo: company.vatNo || "",
            isActive: company.isActive ?? true,
            subscription: {
              currentPeriodStart: company.subscription?.currentPeriodStart || company.currentPeriodStart || "",
              currentPeriodEnd: company.subscription?.currentPeriodEnd || company.currentPeriodEnd || "",
              paymentMethod: company.subscription?.paymentMethod || company.paymentMethod || "",
              paymentId: company.subscription?.paymentId || company.paymentId || "",
              paymentStatus: company.subscription?.paymentStatus || company.paymentStatus || "",
              amountPaid: company.subscription?.amountPaid || company.amountPaid || ""
            }
          }));
          // Set the plan type in separate state
          setSelectedPlanType(company.type || "");
        })
        .catch(err => {
          showToast("Failed to load company details.", "error");
        })
        .finally(() => hideLoading());
    }
  }, [companyId]);

  // Fetch custom rights for override feature in edit mode
  useEffect(() => {
    if (companyId && isEditMode) {
      const fetchCustomRights = async () => {
        try {
          setIsLoadingCustomRights(true);
          const response = await getCustomRights(companyId);
          const data = response.data || {};
          
          // Set override state from API
          const overrideValue = data.overridePlanRights || false;
          setOverridePlanRights(overrideValue);
          
          // If override is true and rights exist, merge with default structure
          // Otherwise initialize with default structure
          if (data.rights && Object.keys(data.rights).length > 0) {
            // Merge API rights with default structure to ensure all fields exist
            setCustomRights({
              ...getDefaultCustomRights(),
              ...data.rights
            });
          } else {
            setCustomRights(getDefaultCustomRights());
          }
        } catch (error) {
          console.error('Error fetching custom rights:', error);
          // If there's an error (e.g., no custom rights exist), default to false
          // Initialize with all rights set to false using static structure
          setOverridePlanRights(false);
          setCustomRights(getDefaultCustomRights());
        } finally {
          setIsLoadingCustomRights(false);
        }
      };
      fetchCustomRights();
    }
  }, [companyId, isEditMode, getDefaultCustomRights]);

  // Fetch rights for edit mode (module rights from plan)
  // Only fetch if override is NOT enabled and after custom rights have been checked
  useEffect(() => {
    // Wait for custom rights to be loaded first (isLoadingCustomRights should be false)
    // Also check if we don't already have dynamicRights to prevent refetching
    if (companyId && isEditMode && !overridePlanRights && !isLoadingCustomRights && !dynamicRights) {
      const fetchRights = async () => {
        try {
          setIsLoadingRights(true);
          const response = await getAllRightsEdit(companyId);
          const rightsData = response.data || {};
          setDynamicRights(rightsData);
          
          // Update formData rights with fetched data
          setFormData(prev => ({
            ...prev,
            rights: rightsData
          }));
        } catch (error) {
          console.error('Error fetching rights:', error);
          showToast("Failed to load module rights.", "error");
        } finally {
          setIsLoadingRights(false);
        }
      };
      fetchRights();
    }
  }, [companyId, isEditMode, overridePlanRights, isLoadingCustomRights, dynamicRights, showToast]);

  // Initialize customRights with static default structure when override is enabled
  useEffect(() => {
    if (overridePlanRights && !customRights) {
      // Initialize with static default rights structure (all false)
      setCustomRights(getDefaultCustomRights());
    }
  }, [overridePlanRights, customRights, getDefaultCustomRights]);

  // Validate form
  useEffect(() => {
    const requiredFields = ['companyName', 'address', 'state', 'zip', 'country', 'email', 'phno', 'planId'];
    const isValid = requiredFields.every(field =>
      formData[field] && formData[field].toString().trim() !== ""
    );
    setIsFormValid(isValid);
  }, [formData]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested subscription fields
    if (name.startsWith('subscription.')) {
      const fieldName = name.split('.')[1];
      
      // Convert amountPaid to number
      let processedValue = value;
      if (fieldName === 'amountPaid') {
        processedValue = value === '' ? '' : Number(value);
      }
      
      setFormData(prev => ({
        ...prev,
        subscription: {
          ...prev.subscription,
          [fieldName]: processedValue
        }
      }));
    } else if (name === 'planId') {
      // When plan is selected, also set the plan type in separate state
      const selectedPlan = plans.find(plan => plan._id === value);
      const planType = selectedPlan ? selectedPlan.type : '';
      
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      setSelectedPlanType(planType);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle override plan rights toggle change
  const handleOverrideToggle = async () => {
    const newOverrideValue = !overridePlanRights;
    
    try {
      // Update state first
      setOverridePlanRights(newOverrideValue);
      
      // If turning override ON, clear dynamicRights so they can be fetched fresh when turned OFF
      if (newOverrideValue) {
        setDynamicRights(null);
      }
      
      // Only send overridePlanRights and companyId when toggling
      const payload = {
        overridePlanRights: newOverrideValue,
        companyId: companyId
      };
      
      // Save the override preference immediately
      await createOrUpdateCustomRights(payload);
      showToast(
        newOverrideValue 
          ? "Override plan rights enabled. Custom rights are now active." 
          : "Override plan rights disabled. Using plan rights.",
        "success"
      );
    } catch (error) {
      console.error('Error toggling override plan rights:', error);
      // Revert the state on error
      setOverridePlanRights(!newOverrideValue);
      showToast("Failed to update override setting. Please try again.", "error");
    }
  };

  // Handle saving custom rights
  const handleSaveCustomRights = async () => {
    if (!companyId || !customRights) return;
    
    try {
      setIsSavingCustomRights(true);
      const payload = {
        overridePlanRights: overridePlanRights,
        rights: customRights,
        companyId: companyId
      };
      
      await createOrUpdateCustomRights(payload);
      showToast("Custom rights saved successfully!", "success");
    } catch (error) {
      console.error('Error saving custom rights:', error);
      showToast("Failed to save custom rights. Please try again.", "error");
    } finally {
      setIsSavingCustomRights(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid || isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      // Prepare form data with proper type conversion
      let submitData = { ...formData };
      
      if (isEditMode) {
        // When override is true, send only the rights that are true from custom rights
        if (overridePlanRights && customRights) {
          // Filter and send only rights that are true
          const trueRights = Object.entries(customRights)
            .filter(([key, value]) => value === true)
            .reduce((acc, [key]) => {
              acc[key] = true;
              return acc;
            }, {});
          submitData.rights = trueRights;
        } else if (!overridePlanRights && dynamicRights) {
          // Use dynamic rights when override is false
          submitData.rights = dynamicRights;
        }
        
        submitData.subscription = {
          ...formData.subscription,
          amountPaid: formData.subscription.amountPaid === '' ? 0 : Number(formData.subscription.amountPaid)
        };
        // Include the type field in edit mode from separate state
        submitData.type = selectedPlanType;
      } else {
        // Remove rights, subscription, and type objects when adding new company
        delete submitData.rights;
        delete submitData.subscription;
        delete submitData.type;
      }
      
      let response;
      if (isEditMode && companyId) {
        response = await updateCompanyBySuperAdmin(companyId, submitData);
      } else {
        response = await addCompanyBySuperAdmin(submitData);
      }

      // Check if response contains duplicate error (even with 201 status)
      if (response?.data?.code === "duplicate") {
        showToast(response.data.message || "Email or Phone Number is already registered", "error");
        setIsSubmitting(false);
        return;
      }

      // Success handling
      if (response && response.data) {
        showToast(isEditMode ? "Company updated successfully!" : "Company added successfully!", "success");
        setTimeout(() => {
          navigate("/home/superadmin");
        }, 1500);
      } else {
        showToast(isEditMode ? "Failed to update company." : "Failed to add company.", "error");
      }
    } catch (error) {
      console.error(isEditMode ? 'Error updating company:' : 'Error adding company:', error);
      
      // Check if it's a duplicate error from backend
      if (error.response?.data?.code === "duplicate") {
        showToast(error.response.data.message || "Email or Phone Number is already registered", "error");
      } else {
        let errorMessage = isEditMode ? "Failed to update company. Please try again." : "Failed to add company. Please try again.";
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        showToast(errorMessage, "error");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="sa-company-form-container">
      <div className="sa-company-form-wrapper">
        <div className="sa-company-form-header">
          <h2 className="sa-company-form-title">{isEditMode ? "Edit Company" : "Add New Company"}</h2>
          <p className="sa-company-form-subtitle">{isEditMode ? "Update the company details below" : "Fill in the company details below"}</p>
        </div>

          <hr className="sa-company-form-divider" />

          <form onSubmit={handleSubmit} className="form-content">
            {/* Basic Company Information Section */}
            <div className="form-section">
              <h3>Basic Information</h3>

              <div className="form-grid">
                <div className="sa-comp-form-group">
                  <label htmlFor="companyName">
                    Company Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    placeholder="Enter company name"
                    required
                  />
                </div>

                <div className="sa-comp-form-group">
                  <label htmlFor="email">
                    Email <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="admin@company.com"
                    required
                  />
                </div>

                <div className="sa-comp-form-group full-width">
                  <label htmlFor="address">
                    Address <span className="required">*</span>
                  </label>
                  <textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter complete address"
                    rows="3"
                    required
                  />
                </div>

                <div className="sa-comp-form-group">
                  <label htmlFor="state">
                    State <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="Enter state"
                    required
                  />
                </div>

                <div className="sa-comp-form-group">
                  <label htmlFor="zip">
                    ZIP Code <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="zip"
                    name="zip"
                    value={formData.zip}
                    onChange={handleInputChange}
                    placeholder="Enter ZIP code"
                    required
                  />
                </div>

                <div className="sa-comp-form-group">
                  <label htmlFor="country">
                    Country <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder="Enter country"
                    required
                  />
                </div>

                <div className="sa-comp-form-group">
                  <label htmlFor="phno">
                    Phone Number <span className="required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phno"
                    name="phno"
                    value={formData.phno}
                    onChange={handleInputChange}
                    placeholder="+91-9876543210"
                    required
                  />
                </div>

                <div className="sa-comp-form-group">
                  <label htmlFor="website">Website</label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="https://www.company.com"
                  />
                </div>
              </div>
            </div>

            {/* Plan & Tax Information Section */}
            <div className="form-section">
              <h3>Plan & Tax Information</h3>

              <div className="form-grid">
                <div className="sa-comp-form-group">
                  <label htmlFor="planId">
                    Plan <span className="required">*</span>
                  </label>
                  <select
                    id="planId"
                    name="planId"
                    value={formData.planId}
                    onChange={handleInputChange}
                    required
                    disabled={isLoadingPlans}
                  >
                    <option value="">
                      {isLoadingPlans ? "Loading plans..." : "Select a plan"}
                    </option>
                    {plans.map((plan) => (
                      <option key={plan._id} value={plan._id}>
                        {plan.name} - ₹{plan.price?.toLocaleString()} ({plan.duration} days)
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sa-comp-form-group">
                  <label htmlFor="gstNo">GST Number</label>
                  <input
                    type="text"
                    id="gstNo"
                    name="gstNo"
                    value={formData.gstNo}
                    onChange={handleInputChange}
                    placeholder="27AAAAA1234A1Z5"
                  />
                </div>

                {/* <div className="sa-comp-form-group">
                  <label htmlFor="vatNo">VAT Number</label>
                  <input
                    type="text"
                    id="vatNo"
                    name="vatNo"
                    value={formData.vatNo}
                    onChange={handleInputChange}
                    placeholder="VAT123456"
                  />
                </div> */}
                <div className="sa-comp-form-group company-status-toggle">
                  <label>Company Status</label>
                  <button
                    type="button"
                    className={`toggle-switch-simple ${formData.isActive ? 'active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, isActive: !prev.isActive }))}
                    disabled={isSubmitting}
                    aria-pressed={formData.isActive}
                    aria-label="Toggle company active status"
                  />
                  <span className="status-text">
                    {formData.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
            
            {isEditMode && (
              <div className="form-section">
                <h3>Subscription Information</h3>
                
                <div className="form-grid">
                  <div className="sa-comp-form-group">
                    <label htmlFor="currentPeriodStart">Current Period Start</label>
                    <input
                      type="datetime-local"
                      id="currentPeriodStart"
                      name="subscription.currentPeriodStart"
                      value={formatDateTimeForInput(formData.subscription.currentPeriodStart)}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="sa-comp-form-group">
                    <label htmlFor="currentPeriodEnd">Current Period End</label>
                    <input
                      type="datetime-local"
                      id="currentPeriodEnd"
                      name="subscription.currentPeriodEnd"
                      value={formatDateTimeForInput(formData.subscription.currentPeriodEnd)}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="sa-comp-form-group">
                    <label htmlFor="paymentMethod">Payment Method</label>
                    {/* <input
                      type="text"
                      id="paymentMethod"
                      name="subscription.paymentMethod"
                      value={formData.subscription.paymentMethod}
                      onChange={handleInputChange}
                      //placeholder="razorpay"
                    /> */}
                    {/* dropdown for payment method */}
                    <select
                      name="subscription.paymentMethod"
                      value={formData.subscription.paymentMethod}
                      onChange={handleInputChange}
                    >
                      <option value="">Select payment method</option>
                      <option value="card">Card</option>
                      <option value="razorpay">Razorpay</option>
                      <option value="netbanking">Net Banking</option>
                      <option value="upi">UPI</option>

                    </select>
                  </div>

                  <div className="sa-comp-form-group">
                    <label htmlFor="paymentId">Payment ID</label>
                    <input
                      type="text"
                      id="paymentId"
                      name="subscription.paymentId"
                      value={formData.subscription.paymentId}
                      onChange={handleInputChange}
                      //placeholder="pay_0987654321"
                    />
                  </div>

                  <div className="sa-comp-form-group">
                    <label htmlFor="paymentStatus">Payment Status</label>
                    {/* <input
                      type="text"
                      id="paymentStatus"
                      name="subscription.paymentStatus"
                      value={formData.subscription.paymentStatus}
                      onChange={handleInputChange}
                      //placeholder="success"
                    /> */}
                    <select
                      name="subscription.paymentStatus"
                      value={formData.subscription.paymentStatus}
                      onChange={handleInputChange}
                    >
                      <option value="">Select payment status</option>
                      <option value="Success">Success</option>
                      <option value="Failed">Failed</option>
                      <option value="Pending">Pending</option>
                      <option value="Free Trial">Free Trial</option>
                    </select>
                  </div>

                  <div className="sa-comp-form-group">
                    <label htmlFor="amountPaid">Amount Paid (₹)</label>
                    <input
                      type="number"
                      id="amountPaid"
                      name="subscription.amountPaid"
                      value={formData.subscription.amountPaid}
                      onChange={handleInputChange}
                      //placeholder="999"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Module Visibility Section - Only show in edit mode */}
            {isEditMode && (
              <div className="form-section visibility-section">
                <div className="module-rights-header">
                  <h3>Module Rights</h3>
                  
                  {/* Override Plan Rights Toggle */}
                  <div className="override-toggle-container">
                    <label className="override-toggle-label">
                      <span className="override-label-text">Override Plan Rights</span>
                      <div className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={overridePlanRights}
                          onChange={handleOverrideToggle}
                          disabled={isLoadingCustomRights}
                        />
                        <span className="slider" />
                      </div>
                    </label>
                  </div>
                </div>

                {(overridePlanRights ? isLoadingCustomRights : isLoadingRights) ? (
                  <div className="loading-rights">Loading module rights...</div>
                ) : overridePlanRights ? (
                  // Show Custom Rights when override is enabled
                  <>
                    <div className="custom-rights-info">
                      <p>Custom rights are enabled. You can modify individual module permissions below.</p>
                    </div>
                    <div className="visibility-toggles">
                      {customRights && Object.entries(customRights).map(([key, value]) => (
                        <label key={key} className="toggle-label">
                          {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                          <div className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={value}
                              onChange={() => {
                                setCustomRights(prev => ({
                                  ...prev,
                                  [key]: !value,
                                }));
                              }}
                            />
                            <span className="slider" />
                          </div>
                        </label>
                      ))}
                    </div>
                    <div className="custom-rights-actions">
                      <button
                        type="button"
                        className="btn-save-custom-rights"
                        onClick={handleSaveCustomRights}
                        disabled={isSavingCustomRights || !customRights}
                      >
                        {isSavingCustomRights ? "Saving..." : "Save Custom Rights"}
                      </button>
                    </div>
                  </>
                ) : (
                  // Show Plan Module Rights when override is disabled
                  <>
                    <div className="plan-rights-info">
                      <p>Module rights are inherited from the selected plan.</p>
                    </div>
                    <div className="visibility-toggles">
                      {Object.entries(dynamicRights || {}).map(([key, value]) => (
                        <label key={key} className="toggle-label">
                          {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                          <div className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={value}
                              onChange={() => {
                                setDynamicRights(prev => ({
                                  ...prev,
                                  [key]: !value,
                                }));
                              }}
                            />
                            <span className="slider" />
                          </div>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => navigate("/home/superadmin")}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`btn-submit ${!isFormValid ? 'disabled' : ''} ${isSubmitting ? 'submitting' : ''}`}
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting ? (
                  isEditMode ? "Updating..." : "Saving..."
                ) : (
                  isEditMode ? "Update Company" : "Save Company"
                )}
              </button>
            </div>
          </form>

        </div>
    </div>
  );
};